import React, { useEffect, useState } from 'react'
import { Badge, Label, Select, Spinner, TextInput, ToggleSwitch } from 'flowbite-react'
import axios from 'axios'
import { API_BASE } from '../constants/inviteCode'
import { toastApiError } from '../utils/apiError'

const EMPTY_FORM = {
  name: '',
  onboarded: false,
  street_number: '',
  street: '',
  city: '',
  state: '',
  county: '',
  country_iso2: 'US',
  contact_email: '',
  contact_phone: '',
  official_website: '',
  official_domain: '',
  logo_url: '',
  osm_id: '',
  wikidata_q_id: '',
  authority_type_id: '',
  website_vendor_id: '',
  timezone: '',
}

const toFormState = (authority = {}) => ({
  name: authority.name ?? '',
  onboarded: Boolean(authority.onboarded),
  street_number: authority.street_number ?? '',
  street: authority.street ?? '',
  city: authority.city ?? '',
  state: authority.state ?? '',
  county: authority.county ?? '',
  country_iso2: authority.country_iso2 ?? 'US',
  contact_email: authority.contact_email ?? '',
  contact_phone: authority.contact_phone ?? '',
  official_website: authority.official_website ?? '',
  official_domain: authority.official_domain ?? '',
  logo_url: authority.logo_url ?? '',
  osm_id: authority.osm_id ?? '',
  wikidata_q_id: authority.wikidata_q_id ?? '',
  authority_type_id: authority.authority_type_id ?? '',
  website_vendor_id: authority.website_vendor_id ?? '',
  timezone: authority.timezone ?? '',
})

const toPayload = (formData) => {
  const payload = {
    name: formData.name.trim(),
    onboarded: Boolean(formData.onboarded),
    street_number: formData.street_number.trim() || null,
    street: formData.street.trim() || null,
    city: formData.city.trim() || null,
    state: formData.state.trim() || null,
    county: formData.county.trim() || null,
    country_iso2: formData.country_iso2.trim() || null,
    contact_email: formData.contact_email.trim() || null,
    contact_phone: formData.contact_phone.trim() || null,
    official_website: formData.official_website.trim() || null,
    official_domain: formData.official_domain.trim() || null,
    logo_url: formData.logo_url.trim() || null,
    osm_id: formData.osm_id.trim() || null,
    wikidata_q_id: formData.wikidata_q_id.trim() || null,
    timezone: formData.timezone.trim() || null,
  }

  if (formData.authority_type_id !== '' && formData.authority_type_id != null) {
    payload.authority_type_id = Number(formData.authority_type_id)
  } else {
    payload.authority_type_id = null
  }

  if (formData.website_vendor_id !== '' && formData.website_vendor_id != null) {
    payload.website_vendor_id = Number(formData.website_vendor_id)
  } else {
    payload.website_vendor_id = null
  }

  return payload
}

const ReadOnlyValue = ({ value, fallback = '—' }) => (
  <div className="text-gray-900 dark:text-white break-words">
    {value || fallback}
  </div>
)

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="mb-1 block">{label}</Label>
    {children}
  </div>
)

const AuthorityForm = ({
  authority,
  isEditing,
  formData,
  onChange,
  token,
  showSystemInfo = true,
  mapAside = null,
}) => {
  const [authorityTypes, setAuthorityTypes] = useState([])
  const [websiteVendors, setWebsiteVendors] = useState([])
  const [countries, setCountries] = useState([])
  const [lookupsLoading, setLookupsLoading] = useState(true)

  useEffect(() => {
    const fetchLookups = async () => {
      setLookupsLoading(true)
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const [typesRes, vendorsRes, countriesRes] = await Promise.all([
          axios.get(`${API_BASE}/authority-types`, { headers }),
          axios.get(`${API_BASE}/website-vendors`, { headers }),
          axios.get(`${API_BASE}/iso2`, { headers }),
        ])
        setAuthorityTypes(Array.isArray(typesRes.data) ? typesRes.data : [])
        setWebsiteVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : [])
        setCountries(Array.isArray(countriesRes.data) ? countriesRes.data : [])
      } catch (error) {
        toastApiError(error, 'Failed to load form options')
      } finally {
        setLookupsLoading(false)
      }
    }

    fetchLookups()
  }, [token])

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target
    onChange({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    })
  }

  const typeLabel =
    authority?.authority_type?.name ||
    authorityTypes.find((item) => item.id === Number(formData.authority_type_id))?.name
  const vendorLabel =
    authority?.website_vendor?.name ||
    websiteVendors.find((item) => item.id === Number(formData.website_vendor_id))?.name

  if (lookupsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          City Profile
        </h3>
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name *" className="sm:col-span-2">
              {isEditing ? (
                <TextInput
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              ) : (
                <ReadOnlyValue value={formData.name} />
              )}
            </Field>

            <Field label="Authority Type" className="sm:col-span-2 sm:max-w-md">
              {isEditing ? (
                <Select
                  name="authority_type_id"
                  value={formData.authority_type_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select type</option>
                  {authorityTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <ReadOnlyValue value={typeLabel} />
              )}
            </Field>

            <Field label="Website Vendor" className="sm:col-span-2 sm:max-w-md">
              {isEditing ? (
                <Select
                  name="website_vendor_id"
                  value={formData.website_vendor_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select vendor</option>
                  {websiteVendors.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <ReadOnlyValue value={vendorLabel} />
              )}
            </Field>

            <Field label="Onboarded" className="sm:col-span-2">
              {isEditing ? (
                <ToggleSwitch
                  checked={formData.onboarded}
                  label=""
                  aria-label={formData.onboarded ? 'Onboarded' : 'Not onboarded'}
                  onChange={(checked) => onChange({ ...formData, onboarded: checked })}
                />
              ) : (
                <Badge
                  color={formData.onboarded ? 'success' : 'gray'}
                  aria-label={formData.onboarded ? 'Onboarded' : 'Not onboarded'}
                >
                  {formData.onboarded ? 'Yes' : 'No'}
                </Badge>
              )}
            </Field>
          </div>

          {mapAside && (
            <aside className="shrink-0 w-full max-w-[360px]">
              {mapAside}
            </aside>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Location
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['street_number', 'Street Number'],
            ['street', 'Street'],
            ['city', 'City'],
            ['state', 'State'],
            ['county', 'County'],
            ['timezone', 'Timezone'],
          ].map(([name, label]) => (
            <Field key={name} label={label}>
              {isEditing ? (
                <TextInput
                  name={name}
                  value={formData[name]}
                  onChange={handleInputChange}
                />
              ) : (
                <ReadOnlyValue value={formData[name]} />
              )}
            </Field>
          ))}

          <Field label="Country">
            {isEditing ? (
              <Select
                name="country_iso2"
                value={formData.country_iso2}
                onChange={handleInputChange}
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.iso2} value={country.iso2}>
                    {country.name} ({country.iso2})
                  </option>
                ))}
              </Select>
            ) : (
              <ReadOnlyValue value={formData.country_iso2} />
            )}
          </Field>
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Contact & Web
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['contact_email', 'Contact Email'],
            ['contact_phone', 'Contact Phone'],
            ['official_website', 'Official Website'],
            ['official_domain', 'Official Domain'],
            ['logo_url', 'Logo URL'],
          ].map(([name, label]) => (
            <Field key={name} label={label} className={name === 'logo_url' ? 'md:col-span-2' : ''}>
              {isEditing ? (
                <TextInput
                  name={name}
                  value={formData[name]}
                  onChange={handleInputChange}
                />
              ) : name === 'official_website' || name === 'logo_url' ? (
                valueLink(formData[name])
              ) : (
                <ReadOnlyValue value={formData[name]} />
              )}
            </Field>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          External IDs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['osm_id', 'OpenStreetMap ID'],
            ['wikidata_q_id', 'Wikidata Q ID'],
          ].map(([name, label]) => (
            <Field key={name} label={label}>
              {isEditing ? (
                <TextInput
                  name={name}
                  value={formData[name]}
                  onChange={handleInputChange}
                />
              ) : (
                <ReadOnlyValue value={formData[name]} />
              )}
            </Field>
          ))}
        </div>
      </section>

      {showSystemInfo && authority?.id && !isEditing && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            System
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Field label="Authority ID">
              <ReadOnlyValue value={String(authority.id)} />
            </Field>
            <Field label="Date Added">
              <ReadOnlyValue
                value={
                  authority.date_added
                    ? new Date(authority.date_added).toLocaleString()
                    : ''
                }
              />
            </Field>
            <Field label="Date Updated">
              <ReadOnlyValue
                value={
                  authority.date_updated
                    ? new Date(authority.date_updated).toLocaleString()
                    : ''
                }
              />
            </Field>
          </div>
        </section>
      )}
    </div>
  )
}

const valueLink = (value) => {
  if (!value) return <ReadOnlyValue value="" />
  const href = value.startsWith('http') ? value : `https://${value}`
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 break-all"
    >
      {value}
    </a>
  )
}

export { EMPTY_FORM, toFormState, toPayload }
export default AuthorityForm
